import React, { useCallback, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { formatDateShort } from '~/services/TimeService';

import type { SalesforceSurchargePeriod } from '~/types/surcharges';

import currencyFormatter from '~/utils/currencyFormatter';
import { daysOfWeekMessage } from '~/utils/daysOfWeek';

import { useDealInformationTabCount } from '../../DealInformationTabCounts';
import TabPanelShell from '../../shared/TabPanelShell';
import { getDateRangeAvailability } from '../../shared/dateRangeAvailability';
import { useFilteredByExpiry } from '../../shared/useFilteredByExpiry';

import BooleanChip from './BooleanChip';
import MatchedRoomRateCards from './MatchedRoomRateCards';
import UnmatchedRoomRateCards from './UnmatchedRoomRateCards';
import {
  SURCHARGES_TABLE_COLUMN_COUNT,
  UNMATCHED_ROW_ID,
  dropdownContentSx,
  getDropdownCellSx,
  getUniqueRoomRateCount,
  handleExpandRowKeyDown,
  toggleExpandedRow,
} from './surchargeTabLayout';
import {
  buildAppliedRoomsBySurchargeId,
  getApplicableNightCount,
  getUnmatchedReservationSurcharges,
  groupReservationSurcharges,
} from './surchargeUtils';
import type { SurchargesTabProps } from './types';

export default function SurchargesTab({
  canAddSurcharges,
  error,
  isLoading,
  isLoadingReservationSurcharges,
  onAddSurcharges,
  reservationSurcharges,
  reservationSurchargesError,
  surcharges,
}: SurchargesTabProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [hideExpired, setHideExpired] = useState(true);
  useDealInformationTabCount('surcharges', isLoading || error ? undefined : surcharges.length);
  const appliedRoomsBySurchargeId = useMemo(
    () =>
      buildAppliedRoomsBySurchargeId({
        reservationSurcharges,
        surcharges,
      }),
    [reservationSurcharges, surcharges],
  );
  const unmatchedReservationSurcharges = useMemo(
    () => getUnmatchedReservationSurcharges(reservationSurcharges),
    [reservationSurcharges],
  );
  const unmatchedRoomRateCount = useMemo(
    () =>
      groupReservationSurcharges(unmatchedReservationSurcharges).reduce(
        (count, room) => count + room.rateGroups.length,
        0,
      ),
    [unmatchedReservationSurcharges],
  );
  const now = dayjs.tz();
  const isExpired = useCallback(
    (surcharge: SalesforceSurchargePeriod) => getDateRangeAvailability(surcharge, now) === 'expired',
    [now],
  );
  const visibleSurcharges = useFilteredByExpiry(surcharges, isExpired, hideExpired);

  if (isLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <CircularProgress />
        <Typography color="text.secondary">Loading surcharges.</Typography>
      </Stack>
    );
  }

  if (error) {
    return <Alert severity="error">Unable to load Salesforce surcharges.</Alert>;
  }

  return (
    <TabPanelShell
      title="Surcharges"
      actions={
        <Button
          disabled={!canAddSurcharges}
          onClick={onAddSurcharges}
          startIcon={<AddIcon />}
          sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
          variant="contained"
        >
          Apply Salesforce surcharges
        </Button>
      }
      contentSx={{ p: 0 }}
      titleActions={
        <Button
          size="small"
          variant="outlined"
          startIcon={hideExpired ? <VisibilityIcon /> : <VisibilityOffIcon />}
          onClick={() => setHideExpired((current) => !current)}
          sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
        >
          {hideExpired ? 'Show expired blocks' : 'Hide expired blocks'}
        </Button>
      }
    >
      <Stack spacing={1} sx={{ px: 3, pt: 3, pb: 1.5 }}>
        <Typography color="text.secondary" variant="body2">
          Expand a Salesforce surcharge to see the room rates with a matching surcharge block. Applying Salesforce
          surcharges replaces the existing blocks on each room rate you select, including blocks with no matching
          Salesforce surcharge on this opportunity.
        </Typography>
      </Stack>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden' }}>
        <Table size="small" sx={{ minWidth: 1300 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.14),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>Parent Salesforce surcharges</TableCell>
            </TableRow>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
                  color: 'info.dark',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0,
                  textTransform: 'uppercase',
                },
              }}
            >
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Days of week</TableCell>
              <TableCell align="center">Nights</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Converted Cost</TableCell>
              <TableCell align="right">Sell Price</TableCell>
              <TableCell align="right">Margin</TableCell>
              <TableCell>Collection</TableCell>
              <TableCell align="center">Accumulate</TableCell>
              <TableCell align="center">Overlapping</TableCell>
              <TableCell align="center">Matching room rates</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {surcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    No Salesforce surcharges found for this offer.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {surcharges.length > 0 && visibleSurcharges.length === 0 && (
              <TableRow>
                <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT}>
                  <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                    All Salesforce surcharges for this offer are expired.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {visibleSurcharges.map((surcharge) => {
              const margin = surcharge.margin;
              const hasMargin = typeof margin === 'number';
              const matchingAssignments = appliedRoomsBySurchargeId[surcharge.id] ?? [];
              const isExpanded = expandedRows.has(surcharge.id);

              return (
                <React.Fragment key={surcharge.id}>
                  <TableRow
                    hover
                    role="button"
                    sx={{ cursor: 'pointer' }}
                    tabIndex={0}
                    onClick={() => setExpandedRows((current) => toggleExpandedRow(current, surcharge.id))}
                    onKeyDown={(event) =>
                      handleExpandRowKeyDown(event, () =>
                        setExpandedRows((current) => toggleExpandedRow(current, surcharge.id)),
                      )
                    }
                  >
                    <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
                    <TableCell>{surcharge.name}</TableCell>
                    <TableCell>{formatDateShort(surcharge.start_date)}</TableCell>
                    <TableCell>{formatDateShort(surcharge.end_date)}</TableCell>
                    <TableCell>{daysOfWeekMessage(surcharge)}</TableCell>
                    <TableCell align="center">{getApplicableNightCount(surcharge)}</TableCell>
                    <TableCell align="right">
                      {currencyFormatter(surcharge.currency_code, surcharge.cost_price)}
                    </TableCell>
                    <TableCell align="right">{currencyFormatter('AUD', surcharge.converted_cost)}</TableCell>
                    <TableCell align="right">
                      {currencyFormatter(surcharge.currency_code, surcharge.sell_price)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        color={hasMargin ? (margin >= 20 ? 'success.main' : 'warning.main') : 'error.main'}
                        fontWeight={700}
                        variant="body2"
                      >
                        {hasMargin ? `${margin.toFixed(1)}%` : 'Missing'}
                      </Typography>
                    </TableCell>
                    <TableCell>{surcharge.collected_by}</TableCell>
                    <TableCell align="center">
                      <BooleanChip value={surcharge.accumulate} />
                    </TableCell>
                    <TableCell align="center">
                      <BooleanChip value={surcharge.overlapping} />
                    </TableCell>
                    <TableCell align="center">
                      {isLoadingReservationSurcharges ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Chip label={getUniqueRoomRateCount(matchingAssignments)} size="small" variant="outlined" />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT} sx={getDropdownCellSx(isExpanded)}>
                      <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
                        <Box sx={dropdownContentSx}>
                          {reservationSurchargesError ? (
                            <Alert severity="error">Unable to load room-rate surcharges.</Alert>
                          ) : (
                            <MatchedRoomRateCards
                              assignments={matchingAssignments}
                              hideExpired={hideExpired}
                              now={now}
                            />
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            <TableRow
              hover
              role="button"
              sx={{
                cursor: 'pointer',
                '& .MuiTableCell-root': { bgcolor: 'grey.50' },
              }}
              tabIndex={0}
              onClick={() => setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID))}
              onKeyDown={(event) =>
                handleExpandRowKeyDown(event, () =>
                  setExpandedRows((current) => toggleExpandedRow(current, UNMATCHED_ROW_ID)),
                )
              }
            >
              <TableCell>
                {expandedRows.has(UNMATCHED_ROW_ID) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
              </TableCell>
              <TableCell colSpan={SURCHARGES_TABLE_COLUMN_COUNT - 2}>
                <Typography fontWeight={700} variant="body2">
                  Room Rate Surcharges with No Matching Salesforce Surcharge
                </Typography>
              </TableCell>
              <TableCell align="center">
                {isLoadingReservationSurcharges ? (
                  <CircularProgress size={16} />
                ) : (
                  <Chip label={unmatchedRoomRateCount} size="small" variant="outlined" />
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                colSpan={SURCHARGES_TABLE_COLUMN_COUNT}
                sx={getDropdownCellSx(expandedRows.has(UNMATCHED_ROW_ID))}
              >
                <Collapse unmountOnExit in={expandedRows.has(UNMATCHED_ROW_ID)} sx={{ width: '100%' }} timeout="auto">
                  <Box sx={dropdownContentSx}>
                    {isLoadingReservationSurcharges ? (
                      <Stack alignItems="center" direction="row" justifyContent="center" spacing={1} sx={{ py: 2 }}>
                        <CircularProgress size={16} />
                        <Typography color="text.secondary" variant="body2">
                          Loading room-rate surcharges.
                        </Typography>
                      </Stack>
                    ) : reservationSurchargesError ? (
                      <Alert severity="error">Unable to load room-rate surcharges.</Alert>
                    ) : (
                      <UnmatchedRoomRateCards
                        assignments={unmatchedReservationSurcharges}
                        hideExpired={hideExpired}
                        now={now}
                      />
                    )}
                  </Box>
                </Collapse>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </TabPanelShell>
  );
}
